import { getWorkspaceTheme } from '../utils/workspaceTheme';
import React, { useEffect, useState } from 'react';
import {
  AppBar, Box, CssBaseline, Drawer, IconButton, List, ListItem, Tooltip, Avatar,
  ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography,
  Menu, MenuItem, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon, Business as BusinessIcon,
  CreditCard as BillingIcon, ReceiptLong as InvoiceIcon,
  Logout, Menu as MenuIcon, MenuOpen as MenuOpenIcon,
  People as PeopleIcon, Settings as SettingsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeContext } from '../contexts/ThemeContext';
import NotificationBell from '../components/NotificationBell';
import ThemeSettingsButton from '../components/ThemeSettingsButton';

const drawerWidth = 264;


const menuItems = [
  { text: 'Tenant Overview', icon: <DashboardIcon />, path: '/tenant', roles: ['tenant_admin', 'tenant_support', 'tenant_billing'] },
  { text: 'Companies', icon: <BusinessIcon />, path: '/tenant/companies', roles: ['tenant_admin', 'tenant_support'] },
  { text: 'Billing', icon: <BillingIcon />, path: '/tenant/billing', roles: ['tenant_admin', 'tenant_billing'] },
  { text: 'Invoices', icon: <InvoiceIcon />, path: '/tenant/invoices', roles: ['tenant_admin', 'tenant_billing'] },
  { text: 'Team', icon: <PeopleIcon />, path: '/tenant/users', roles: ['tenant_admin'] },
  { text: 'Settings', icon: <SettingsIcon />, path: '/tenant/settings', roles: ['tenant_admin', 'tenant_support', 'tenant_billing'] },
];

interface TenantLayoutProps { children: React.ReactNode; }

const TenantLayout: React.FC<TenantLayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('TenantLayout:sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('TenantLayout:sidebarCollapsed', String(sidebarCollapsed));
    } catch {
      // The toggle still works when browser storage is unavailable.
    }
  }, [sidebarCollapsed]);

  const { user, logout } = useAuth();
  const { mode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = getWorkspaceTheme(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  useEffect(() => { setMobileOpen(false); }, [location.pathname, isMobile]);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [location.pathname]);
  const desktopDrawerWidth = sidebarCollapsed ? 0 : drawerWidth;
  const navigationGroups = [
    { label: 'OVERVIEW', paths: ['/tenant', '/tenant/companies'] },
    { label: 'MANAGEMENT', paths: ['/tenant/billing', '/tenant/invoices', '/tenant/users', '/tenant/settings'] },
  ];
  const navigationItems = menuItems.filter(item => item.roles.includes(user?.role || ''));
  const pageTitle = navigationItems.find(item => item.path === location.pathname)?.text || 'Tenant workspace';


  const drawer = (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', bgcolor: colors.sidebar }}>
      <Box sx={{ px: 3, height: 80, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src="/Scetru-Logo-white.png" alt="Scetru" sx={{ width: 112, height: 38, objectFit: 'contain' }} />
        <Typography sx={{ color: colors.sidebarMuted, fontSize: 10, letterSpacing: '0.15em', ml: 'auto' }}>FLEET</Typography>
      </Box>
      <Box sx={{ mx: 2, mb: 1, p: 1.5, bgcolor: colors.sidebarHover, border: `1px solid ${colors.sidebarBorder}`, borderRadius: 2.5 }}>
          <Typography sx={{ color: colors.sidebarText, fontWeight: 650, fontSize: 13 }}>Tenant administration</Typography>
          <Typography sx={{ color: colors.sidebarMuted, fontSize: 11 }}>Manage your workspace</Typography>
        </Box>
      <Box sx={{ flexGrow: 1, px: 1.5, pb: 2 }}>
        {navigationGroups.map(group => {
          const items = navigationItems.filter(item => group.paths.includes(item.path));
          if (!items.length) return null;
          return <Box key={group.label} sx={{ mt: 2.5 }}>
            <Typography sx={{ px: 1.5, mb: 1, color: colors.sidebarMuted, fontSize: 9, fontWeight: 700, letterSpacing: '0.14em' }}>{group.label}</Typography>
            <List disablePadding>
              {items.map(item => <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton selected={location.pathname === item.path}
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  sx={{ minHeight: 44, px: 1.5, borderRadius: 2, color: colors.sidebarText,
                    '& .MuiListItemIcon-root': { color: colors.sidebarMuted },
                    '&:hover': { bgcolor: colors.sidebarHover, color: '#FFFFFF' },
                    '&.Mui-selected': { bgcolor: colors.selected, color: '#FFFFFF', boxShadow: '0 4px 12px #00000018',
                      '&:hover': { bgcolor: colors.selectedHover }, '& .MuiListItemIcon-root': { color: '#FFFFFF' } },
                  }}>
                  <ListItemIcon sx={{ minWidth: 34, '& svg': { fontSize: 19 } }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 12.5, fontWeight: location.pathname === item.path ? 650 : 450 }} />
                </ListItemButton>
              </ListItem>)}
            </List>
          </Box>;
        })}
      </Box>
      <Box sx={{ mx: 3, py: 2.5, borderTop: `1px solid ${colors.sidebarBorder}` }}>
        <Typography sx={{ color: colors.sidebarMuted, fontSize: 10 }}>Powered by <Box component="span" sx={{ color: colors.sidebarText, fontWeight: 700 }}>Scetru</Box></Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <AppBar position="fixed" sx={{
        width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
        ml: { md: `${desktopDrawerWidth}px` },
        background: colors.header,
          color: 'text.primary',
          boxShadow: 'none', borderBottom: '1px solid',
          borderColor: colors.headerBorder,
        zIndex: (t) => t.zIndex.drawer + 1,
        '& .MuiIconButton-root': { color: colors.avatarText },

      }}>
        <Toolbar sx={{ justifyContent: 'space-between', gap: { xs: 0.5, sm: 1.5 }, px: { xs: 1.5, sm: 3 } }}>
          <Tooltip title={isMobile ? 'Open sidebar' : sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <IconButton
              edge="start"
              aria-label={isMobile ? 'Open sidebar' : sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={isMobile ? mobileOpen : !sidebarCollapsed}
              onClick={() => {
                if (isMobile) setMobileOpen(open => !open);
                else setSidebarCollapsed(collapsed => !collapsed);
              }}
            >
              {!isMobile && !sidebarCollapsed ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700 }}>{pageTitle}</Typography>
              <Typography noWrap sx={{ fontSize: 10, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>Scetru Fleet / Tenant workspace</Typography>
            </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 }, ml: 'auto', flexShrink: 0 }}>
            <ThemeSettingsButton />
            <NotificationBell />
            <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' }, fontSize: 12, fontWeight: 600, ml: 1.5 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton aria-label="Open account menu" size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: colors.avatar, color: colors.avatarText, fontSize: 12, fontWeight: 700 }}>{user?.firstName?.[0]}{user?.lastName?.[0]}</Avatar>
            </IconButton>
          </Box>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem disabled><Typography variant="body2">{user?.firstName} {user?.lastName}</Typography></MenuItem>
            <MenuItem disabled><Typography variant="body2" sx={{ color: 'text.secondary' }}>{user?.email}</Typography></MenuItem>
            <Divider />
            <MenuItem onClick={async () => { await logout(); navigate('/login'); }}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: desktopDrawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
              zIndex: (t) => t.zIndex.drawer + 2,
            '& .MuiDrawer-paper': { width: `min(${drawerWidth}px, 88vw)`, backgroundColor: colors.sidebar },
          }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{
            display: { xs: 'none', md: sidebarCollapsed ? 'none' : 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth, borderRight: '1px solid',
              borderColor: colors.sidebarBorder,
              backgroundColor: colors.sidebar,
            },
          }}
          open>
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" className="workspace-content" sx={{
        minWidth: 0, maxWidth: '100%',
        flexGrow: 1, p: { xs: 1.5, sm: 3 },
        width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
        minHeight: '100dvh',
        backgroundColor: 'background.default',
        color: 'text.primary',
      }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default TenantLayout;
